import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

function ProgramManagement() {
  const [programs, setPrograms] = useState([]);
  const [newProgramName, setNewProgramName] = useState('');
  const [newProgramDescription, setNewProgramDescription] = useState('');
  const [newProgramCategory, setNewProgramCategory] = useState('');
  const [newProgramDownloadUrl, setNewProgramDownloadUrl] = useState('');
  const [editingProgram, setEditingProgram] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchPrograms = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/programs/');
      setPrograms(response.data);
    } catch (err) {
      setError('Fehler beim Laden der Programme.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPrograms(); }, [fetchPrograms]);

  const handleAddOrUpdateProgram = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    const programData = { name: newProgramName, description: newProgramDescription, category: newProgramCategory || null, download_url: newProgramDownloadUrl || null };
    try {
      if (editingProgram) {
        await api.put(`/programs/${editingProgram.id}`, programData);
        setEditingProgram(null);
      } else {
        await api.post('/programs/', programData);
      }
      setNewProgramName('');
      setNewProgramDescription('');
      setNewProgramCategory('');
      setNewProgramDownloadUrl('');
      fetchPrograms();
    } catch (err) {
      setError('Fehler beim Speichern des Programms.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProgram = async (id) => {
    if (!window.confirm('Programm wirklich löschen?')) return;
    setLoading(true);
    setError('');
    try {
      await api.delete(`/programs/${id}`);
      fetchPrograms();
    } catch (err) {
      setError('Fehler beim Löschen des Programms.');
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (program) => {
    setEditingProgram(program);
    setNewProgramName(program.name);
    setNewProgramDescription(program.description || '');
    setNewProgramCategory(program.category || '');
    setNewProgramDownloadUrl(program.download_url || '');
  };

  const cancelEditing = () => {
    setEditingProgram(null);
    setNewProgramName('');
    setNewProgramDescription('');
    setNewProgramCategory('');
    setNewProgramDownloadUrl('');
  };

  return (
    <div>
      <div className="page-heading">
        <h2>Programme</h2>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="card" style={{ marginBottom: '24px' }}>
        <p className="card-title">{editingProgram ? 'Programm bearbeiten' : 'Neues Programm'}</p>
        <form onSubmit={handleAddOrUpdateProgram}>
          <div className="form-group">
            <label className="form-label">Name</label>
            <input
              type="text"
              className="form-input"
              value={newProgramName}
              onChange={(e) => setNewProgramName(e.target.value)}
              required
              disabled={loading}
              placeholder="z. B. Familytool"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Beschreibung</label>
            <textarea
              className="form-textarea"
              value={newProgramDescription}
              onChange={(e) => setNewProgramDescription(e.target.value)}
              disabled={loading}
              placeholder="Kurze Beschreibung…"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Kategorie</label>
            <select
              className="form-select"
              value={newProgramCategory}
              onChange={(e) => setNewProgramCategory(e.target.value)}
              disabled={loading}
            >
              <option value="">– Keine –</option>
              <option value="allgemein">Allgemein</option>
              <option value="fivem">FiveM / GTA RP</option>
              <option value="gta-rp">Legacy GTA RP</option>
              <option value="familyboard">Familyboard</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Download-URL <span style={{ color: 'var(--text-dim)', fontWeight: 400 }}>(optional – wird Nutzern nach Discord-Login angezeigt)</span></label>
            <input
              type="url"
              className="form-input"
              value={newProgramDownloadUrl}
              onChange={(e) => setNewProgramDownloadUrl(e.target.value)}
              disabled={loading}
              placeholder="https://…"
            />
          </div>
          <div className="form-actions">
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Speichern…' : (editingProgram ? 'Aktualisieren' : 'Erstellen')}
            </button>
            {editingProgram && (
              <button type="button" onClick={cancelEditing} className="btn btn-secondary">
                Abbrechen
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="section-header">
        <h3>Vorhandene Programme</h3>
        {loading && <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Lädt…</span>}
      </div>

      {programs.length === 0 && !loading ? (
        <div className="empty-state">Noch keine Programme vorhanden.</div>
      ) : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Beschreibung</th>
                <th>API-Key</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {programs.map((program) => (
                <tr key={program.id}>
                  <td style={{ color: 'var(--text-muted)', width: '40px' }}>{program.id}</td>
                  <td style={{ fontWeight: 600 }}>{program.name}</td>
                  <td style={{ color: 'var(--text-muted)' }}>{program.description}</td>
                  <td><span className="mono">{program.api_key}</span></td>
                  <td>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button onClick={() => startEditing(program)} className="btn btn-secondary btn-sm">
                        Bearbeiten
                      </button>
                      <button onClick={() => handleDeleteProgram(program.id)} className="btn btn-danger btn-sm">
                        Löschen
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default ProgramManagement;
